import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import { PROVIDERS } from '../../lib/constants';
import { Method, InsurancePlan, operationsService } from '../../services/api/operationsService';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

// ─── constants ────────────────────────────────────────────────────────────────
const STORAGE_BASE = 'https://kaoxet.net/storage/';

const VEHICLE_TYPES = [
  { id: '1', label: 'Particular' },
  { id: '2', label: 'Comercial' },
  { id: '3', label: 'Motocicleta' },
  { id: '4', label: 'Camión / Pesado' },
];

const fmt = (n: number) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(n);

// ─── component ────────────────────────────────────────────────────────────────
const SegurosPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { wallet }           = useAppSelector((state) => state.wallet as any);
  const { insuranceMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  // ── form fields ─────────────────────────────────────────────────────────────
  const [selectedProvider, setSelectedProvider] = useState('');
  const [plans,            setPlans]            = useState<InsurancePlan[]>([]);
  const [loadingPlans,     setLoadingPlans]      = useState(false);
  const [selectedPlan,     setSelectedPlan]      = useState<InsurancePlan | null>(null);
  const [phoneNumber,      setPhoneNumber]       = useState('');
  const [documentId,       setDocumentId]        = useState('');
  const [chassisNumber,    setChassisNumber]     = useState('');
  const [vehicleType,      setVehicleType]       = useState('');

  // ── status ──────────────────────────────────────────────────────────────────
  const [isProcessing,      setIsProcessing]     = useState(false);
  const [error,             setError]            = useState<string | null>(null);
  const [success,           setSuccess]          = useState<string | null>(null);
  const [showReceiptModal,  setShowReceiptModal]  = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (!fetchedRef.current && (!insuranceMethods || insuranceMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerList: Method[] =
    insuranceMethods && insuranceMethods.length > 0 ? insuranceMethods : PROVIDERS.SEGUROS;

  // ── select provider → fetch plans ───────────────────────────────────────────
  const handleProviderSelect = async (id: string) => {
    setSelectedProvider(id);
    setSelectedPlan(null);
    setPlans([]);
    setError(null);
    setLoadingPlans(true);
    try {
      const res = await operationsService.fetchInsurances(id);
      if (!res.success) throw new Error(res.message || 'Error al consultar planes');
      const raw: any[] = res.data || [];
      const normalized: InsurancePlan[] = raw.map((p: any) => ({
        id:           String(p.id ?? p.planId ?? p.plan_id ?? ''),
        name:         p.name ?? p.nombre ?? p.plan ?? '',
        description:  p.description ?? p.descripcion ?? p.desc ?? '',
        price:        Number(p.price ?? p.precio ?? p.amount ?? p.monto ?? 0),
        vigency:      String(p.vigency ?? p.vigencia ?? p.days ?? '365'),
        vehicle_type: p.vehicle_type ?? p.vehicleType ?? '',
      }));
      setPlans(normalized);
      if (normalized.length === 0) setError('No hay planes disponibles para esta compañía');
    } catch (err: any) {
      setError(err.message || 'Error al consultar los planes de seguro');
    } finally {
      setLoadingPlans(false);
    }
  };

  // ── validation ───────────────────────────────────────────────────────────────
  const isFormValid = () =>
    !!(selectedProvider && selectedPlan && phoneNumber.length === 10 &&
       documentId.length === 11 && chassisNumber && vehicleType);

  // ── submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProvider)          { setError('Seleccione una compañía');            return; }
    if (!selectedPlan)              { setError('Seleccione un plan de seguro');        return; }
    if (phoneNumber.length !== 10)  { setError('Ingrese un número de teléfono válido de 10 dígitos'); return; }
    if (documentId.length !== 11)   { setError('Ingrese un número de cédula válido (11 dígitos)'); return; }
    if (!chassisNumber)             { setError('Ingrese el número de chasis del vehículo'); return; }
    if (!vehicleType)               { setError('Seleccione el tipo de vehículo');     return; }
    if (wallet && wallet.balance < selectedPlan.price) {
      setError('Saldo insuficiente para contratar este seguro'); return;
    }

    setIsProcessing(true); setError(null);
    try {
      const res = await operationsService.sendInsurance(
        selectedPlan.price, phoneNumber, selectedProvider,
        selectedPlan.name, selectedPlan.vigency || '365',
        vehicleType, documentId, chassisNumber
      );
      if (!res.success) throw new Error(res.message || 'Error al contratar el seguro');

      const providerName = providerList.find(p => p.id === selectedProvider)?.name || selectedProvider;
      const transaction: any = {
        id:        res.data?.xid || res.data?.transactionId || `ins-${Date.now()}`,
        xid:       res.data?.xid || res.data?.transactionId || undefined,
        type:      'SEGURO' as const,
        amount:    selectedPlan.price,
        provider:  providerName,
        date:      new Date().toISOString(),
        status:    'COMPLETED' as const,
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        url:       res.data?.url || null,
        details: {
          phoneNumber, document: documentId,
          chassis: chassisNumber, vehicleType,
          planName: selectedPlan.name,
          vigency:  selectedPlan.vigency || '365',
          price:    selectedPlan.price,
        },
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Seguro "${selectedPlan.name}" contratado con éxito`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      // reset
      setSelectedProvider(''); setSelectedPlan(null); setPlans([]);
      setPhoneNumber(''); setDocumentId(''); setChassisNumber(''); setVehicleType('');
    } catch (err: any) {
      setError(err.message || 'Error al procesar la contratación. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
          </svg>
          Seguros
        </h1>
        <p className="text-gray-500 text-sm mt-1">Contrate seguros de vehículos.</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Compañía */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Compañía aseguradora</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {providerList.map((p: Method) => (
              <button key={p.id} type="button" onClick={() => handleProviderSelect(p.id)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-150 hover:border-amber-400"
                style={{
                  borderColor: selectedProvider === p.id ? '#f59e0b' : '#e2e8f0',
                  background:  selectedProvider === p.id ? '#fef3c720' : '#fff',
                }}>
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center overflow-hidden">
                  {p.logo
                    ? <img src={`${STORAGE_BASE}${p.logo}`} alt={p.name} className="w-10 h-10 object-contain"/>
                    : <span className="text-lg font-bold text-slate-400">{p.name.charAt(0)}</span>
                  }
                </div>
                <span className="text-xs font-medium text-slate-600 text-center leading-tight">{p.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Planes */}
        {selectedProvider && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Plan de seguro</p>

            {loadingPlans ? (
              <div className="flex items-center gap-3 py-6 text-slate-400">
                <svg className="animate-spin w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span className="text-sm">Consultando planes disponibles…</span>
              </div>
            ) : plans.length === 0 ? (
              <p className="text-sm text-slate-400 py-4">No hay planes disponibles para esta compañía.</p>
            ) : (
              <div className="space-y-2">
                {plans.map(plan => (
                  <label key={plan.id}
                    className="flex items-center justify-between gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 hover:border-amber-300"
                    style={{
                      borderColor: selectedPlan?.id === plan.id ? '#f59e0b' : '#e2e8f0',
                      background:  selectedPlan?.id === plan.id ? '#fffbeb' : '#fff',
                    }}>
                    <input type="radio" name="plan" className="sr-only"
                      checked={selectedPlan?.id === plan.id}
                      onChange={() => { setSelectedPlan(plan); setError(null); }}/>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800">{plan.name}</p>
                      {plan.description && <p className="text-xs text-slate-500 mt-0.5">{plan.description}</p>}
                      {plan.vigency && <p className="text-xs text-amber-600 mt-1">Vigencia: {plan.vigency} días</p>}
                    </div>
                    <span className="text-lg font-black text-amber-500 shrink-0">{fmt(plan.price)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Datos del asegurado */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Datos del asegurado</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Teléfono (10 dígitos)"
              value={phoneNumber}
              onChange={e => { const v = e.target.value.replace(/\D/g,''); if (v.length <= 10) setPhoneNumber(v); }}
              placeholder="8091234567" type="tel" fullWidth leftAddon="+1"
              useCustomKeyboard={false}
            />
            <Input label="Cédula (11 dígitos)"
              value={documentId}
              onChange={e => { const v = e.target.value.replace(/\D/g,''); if (v.length <= 11) setDocumentId(v); }}
              placeholder="00112345678" type="tel" fullWidth
              useCustomKeyboard={false}
            />
          </div>
        </div>

        {/* Datos del vehículo */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Datos del vehículo</p>
          <div className="space-y-4">
            <Input label="Número de chasis / VIN"
              value={chassisNumber}
              onChange={e => setChassisNumber(e.target.value.toUpperCase())}
              placeholder="1HGBH41JXMN109186" fullWidth
              useCustomKeyboard={false}
            />
            <div>
              <p className="text-sm font-medium text-slate-600 mb-2">Tipo de vehículo</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {VEHICLE_TYPES.map(vt => (
                  <button key={vt.id} type="button" onClick={() => setVehicleType(vt.id)}
                    className="py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all duration-150"
                    style={{
                      borderColor: vehicleType === vt.id ? '#f59e0b' : '#e2e8f0',
                      background:  vehicleType === vt.id ? '#fffbeb' : '#fff',
                      color:       vehicleType === vt.id ? '#b45309' : '#64748b',
                    }}>
                    {vt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Resumen + botón */}
        {selectedPlan && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-amber-700 font-medium">{selectedPlan.name}</p>
              <p className="text-xs text-amber-600">
                {providerList.find(p => p.id === selectedProvider)?.name} · {selectedPlan.vigency || '365'} días
              </p>
            </div>
            <span className="text-2xl font-black text-amber-600">{fmt(selectedPlan.price)}</span>
          </div>
        )}

        <Button type="submit" size="touch" fullWidth isLoading={isProcessing} disabled={!isFormValid()}>
          {isProcessing ? 'Procesando...' : '✓ Contratar Seguro'}
        </Button>
      </form>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </div>
  );
};

export default SegurosPage;
